import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCultures } from '../api/api';
import api from '../api/api';
import './AddProduct.css';

const AVAILABLE_CATEGORIES = [
  'Traditional Attire',
  'Accessories',
  'Jewelry',
  'Headwear',
  'Footwear',
  'Home Décor',
  'Ceremonial Items',
  'Traditional Art',
  'Paintings & Drawings',
  'Sculptures & Carvings',
  'Beadwork',
  'Pottery & Ceramics',
  'Textiles & Fabrics',
  'Musical Instruments',
  'Cultural Books',
  'Educational Materials',
  'Traditional Weapons & Tools',
  'Handcrafted Items',
  'Wall Art & Prints'
];

function AddProduct() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cultures, setCultures] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: AVAILABLE_CATEGORIES[0],
    culture: '',
    colors: '',
    salePercentage: ''
  });

  useEffect(() => {
    fetchCultures();
  }, []);

  const fetchCultures = async () => {
    try {
      const response = await getCultures();
      const culturesArray = Array.isArray(response.data) 
        ? response.data 
        : (response.data.cultures || []);
      setCultures(culturesArray);
      // Set first culture as default
      if (culturesArray.length > 0) {
        setFormData(prev => ({ ...prev, culture: culturesArray[0] }));
      }
    } catch (error) {
      console.error('Error fetching cultures:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.price || !formData.category || !formData.culture) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.price) <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      
      // Append form fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      const response = await api.post('/products', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        alert('Product created successfully!');
        navigate('/admin/dashboard/products');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setError(error.response?.data?.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-product">
      <div className="add-product-header">
        <h2>Add New Product</h2>
        <button onClick={() => navigate('/admin/dashboard/products')} className="btn-secondary">
          ← Back to Products
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="add-product-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label>Product Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Traditional Zulu Beaded Necklace"
            />
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              placeholder="Detailed product description..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price (R) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="299.99"
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {AVAILABLE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Culture *</label>
            <select
              name="culture"
              value={formData.culture}
              onChange={handleChange}
              required
            >
              <option value="">-- Select a culture --</option>
              {cultures.map(culture => (
                <option key={culture} value={culture}>{culture}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Colour Options</label>
            <input
              type="text"
              name="colors"
              value={formData.colors}
              onChange={handleChange}
              placeholder="e.g. Red, Blue, Green (leave empty for no colour options / default)"
            />
            <small>Separate multiple colours with a comma. Leave empty if this product has no colour options.</small>
          </div>

          <div className="form-group">
            <label>Sale % Off</label>
            <input
              type="number"
              name="salePercentage"
              value={formData.salePercentage}
              onChange={handleChange}
              min="0"
              max="100"
              step="1"
              placeholder="e.g. 50 (leave empty for no sale)"
            />
            <small>Leave empty for no sale. Otherwise the product will show a SALE tag and appear in Featured Products.</small>
            {formData.price && formData.salePercentage > 0 && formData.salePercentage <= 100 && (
              <p className="sale-preview">
                <span className="sale-preview__original">R{parseFloat(formData.price).toFixed(2)}</span>
                {' '}
                <span className="sale-preview__sale">
                  R{(parseFloat(formData.price) * (1 - formData.salePercentage / 100)).toFixed(2)}
                </span>
                {' '}
                <span className="sale-preview__pct">({formData.salePercentage}% OFF)</span>
              </p>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating Product...' : 'Create Product'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard/products')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProduct;
